'use client';

import { DatePicker } from '@koda/koda-components/molecules/DatePicker';
import NumberField from '@koda/koda-components/molecules/NumberField';
import { RadioGroup } from '@koda/koda-components/molecules/RadioGroup';
import TextField from '@koda/koda-components/molecules/TextField';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { ReactElement } from 'react';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import { Past5Years } from './consts/dateIntervals';
import { createPodcastDefaults, PODCAST_CONTENT_PATH, podcastSchema } from './podcast.Schema';

export const PodcastComponent = (): ReactElement => {
  const licenseCode = LicenseTypes.Podcast;
  const t = useTranslations(PODCAST_CONTENT_PATH);

  const { values, setField, getError, showErrors } = useLicenseBusiness(licenseCode, {
    schema: podcastSchema,
    defaults: createPodcastDefaults,
  });

  const publishedValue = values?.isPublished;
  const isPublished = publishedValue === true;

  return (
    <>
      <TextField
        label={t('title.label')}
        name="title"
        placeholder={t('title.placeholder')}
        required
        type="text"
        styleVariant="border"
        value={values?.title ?? ''}
        onChange={e => setField('title', e.target.value)}
        error={values?.title || showErrors ? getError('title') : undefined}
        validated={!!values?.title && !getError('title')}
      />

      <RadioGroup
        title={t('published.label')}
        options={[
          { label: t('published.options.yes'), value: 'yes' },
          { label: t('published.options.no'), value: 'no' },
        ]}
        horizontal={true}
        value={values?.isPublished === null ? undefined : publishedValue ? 'yes' : 'no'}
        onValueChange={val => {
          if (val === 'yes') {
            setField('isPublished', true);
            // Clear expectedStreamsRange since it's only relevant when unpublished
            setField('expectedStreamsRange', 'none');
            setField('expectedRevenuePerMonth', '');
          } else {
            setField('isPublished', false);
            // Clear firstEpisodeDate when switching to unpublished (empty string to satisfy type)
            setField('firstEpisodeDate', '');
            setField('averageStreamsPerMonth', '');
            setField('averageRevenuePerMonth', '');
          }
        }}
        error={showErrors ? getError('isPublished') : undefined}
      />

      <section className="space-y-4">
        {!isPublished && (
          <>
            <h2 className="font-graphik text-xl font-medium">{t('streamsSection.title')}</h2>
            <RadioGroup
              title={t('expectedStreams.label')}
              options={[
                { label: t('expectedStreams.options.0-500'), value: '0-500' },
                { label: t('expectedStreams.options.501-5000'), value: '501-5000' },
                { label: t('expectedStreams.options.5000+'), value: '5000+' },
                { label: t('expectedStreams.options.unknown'), value: 'unknown' },
              ]}
              horizontal={false}
              value={values?.expectedStreamsRange ?? undefined}
              onValueChange={val => setField('expectedStreamsRange', val as '0-500' | '501-5000' | '5000+' | 'unknown')}
              error={showErrors ? getError('expectedStreamsRange') : undefined}
            />
            <NumberField
              label={t('streamsSection.expectedRevenuePerMonth.label')}
              name="expectedRevenuePerMonth"
              placeholder={t('streamsSection.expectedRevenuePerMonth.placeholder')}
              formatThousands
              styleVariant="border"
              optionalText={t('streamsSection.expectedRevenuePerMonth.optionalText')}
              value={values?.expectedRevenuePerMonth ?? ''}
              onChange={e => setField('expectedRevenuePerMonth', e.target.value)}
              error={values?.expectedRevenuePerMonth || showErrors ? getError('expectedRevenuePerMonth') : undefined}
              helpText={t('streamsSection.expectedRevenuePerMonth.helpText')}
            />
          </>
        )}

        {isPublished && (
          <>
            <DatePicker
              label={t('firstEpisodeDate.label')}
              name="firstEpisodeDate"
              placeholder={t('firstEpisodeDate.placeholder')}
              disabled={false}
              value={
                values?.firstEpisodeDate && values.firstEpisodeDate !== ''
                  ? new Date(values.firstEpisodeDate)
                  : undefined
              }
              onChange={date => setField('firstEpisodeDate', date ? dayjs(date).format('YYYY-MM-DD') : '')}
              error={values?.firstEpisodeDate || showErrors ? getError('firstEpisodeDate') : undefined}
              validated={!!values?.firstEpisodeDate && !getError('firstEpisodeDate')}
              initialVisibleValue={new Date()}
              interval={Past5Years}
            />
            <h2 className="font-graphik text-xl font-medium">{t('streamsSection.title')}</h2>
            <NumberField
              label={t('streamsSection.averageStreamsPerMonth.label')}
              name="averageStreamsPerMonth"
              placeholder={t('streamsSection.averageStreamsPerMonth.placeholder')}
              formatThousands
              styleVariant="border"
              value={values?.averageStreamsPerMonth ?? ''}
              onChange={e => setField('averageStreamsPerMonth', e.target.value)}
              error={values?.averageStreamsPerMonth || showErrors ? getError('averageStreamsPerMonth') : undefined}
            />
            <NumberField
              label={t('streamsSection.averageRevenuePerMonth.label')}
              name="averageRevenuePerMonth"
              placeholder={t('streamsSection.averageRevenuePerMonth.placeholder')}
              formatThousands
              styleVariant="border"
              value={values?.averageRevenuePerMonth ?? ''}
              onChange={e => setField('averageRevenuePerMonth', e.target.value)}
              error={values?.averageRevenuePerMonth || showErrors ? getError('averageRevenuePerMonth') : undefined}
              helpText={t('streamsSection.expectedRevenuePerMonth.helpText')}
            />
          </>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="font-graphik text-xl font-medium">{t('contentSection.title')}</h2>
        <TextField
          label={
            isPublished
              ? t('contentSection.averageEpisodeDuration.labelPublished')
              : t('contentSection.averageEpisodeDuration.labelUnpublished')
          }
          name="averageEpisodeDuration"
          placeholder={t('contentSection.averageEpisodeDuration.placeholder')}
          type="text"
          styleVariant="border"
          optionalText={isPublished ? undefined : t('contentSection.averageEpisodeDuration.optionalText')}
          error={values?.averageEpisodeDuration || showErrors ? getError('averageEpisodeDuration') : undefined}
          value={values?.averageEpisodeDuration ?? ''}
          onChange={e => setField('averageEpisodeDuration', e.target.value)}
        />
        <TextField
          label={
            isPublished
              ? t('contentSection.averageMusicMinutesPerEpisode.labelPublished')
              : t('contentSection.averageMusicMinutesPerEpisode.labelUnpublished')
          }
          name="averageMusicMinutesPerEpisode"
          placeholder={t('contentSection.averageMusicMinutesPerEpisode.placeholder')}
          type="text"
          error={
            values?.averageMusicMinutesPerEpisode || showErrors ? getError('averageMusicMinutesPerEpisode') : undefined
          }
          styleVariant="border"
          optionalText={isPublished ? undefined : t('contentSection.averageMusicMinutesPerEpisode.optionalText')}
          value={values?.averageMusicMinutesPerEpisode ?? ''}
          onChange={e => setField('averageMusicMinutesPerEpisode', e.target.value)}
          helpText={t('contentSection.averageMusicMinutesPerEpisode.helpText')}
        />
      </section>
    </>
  );
};
